<template>
  <v-dialog
    v-model="isVisible"
    @click:outside="maybeClose"
    @keydown.esc="maybeClose"
    :fullscreen="fullscreen"
    :max-width="fullscreen ? null : maxWidth"
    :scrim="scrim || undefined"
    persistent
  >
    <v-card
      border
      tile
    >
      <v-card-title v-if="titleOverride || title">
        <v-row
          class="d-flex align-center justify-start"
          dense
        >
          <v-col
            class="wrap-title"
            cols="10"
            tabindex="0"
          >
            <div class="wrap-text">
              {{ $bct(titleOverride || title) }}
            </div>
          </v-col>

          <v-col
            class="d-flex justify-end"
            cols="2"
            tabindex="0"
          >
            <slot name="title-actions" />

            <slot name="close">
              <v-btn
                v-if="closeable"
                @click="close"
                :aria-label="$bct('Close dialog')"
                icon="close"
                variant="text"
              />
            </slot>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text :class="textClass || defaultTextClass">
        {{ $bct(instructionText) }}
      </v-card-text>

      <v-card-text>
        <v-row>
          <v-col>
            <v-radio-group
              v-model="selectedChild"
              hide-details
            >
              <div
                v-for="(child, index) in children"
                :key="child.id"
              >
                <RadioButton
                  :key="child.id"
                  :gap="radioButtonGap(index)"
                  :subtitle="`${$bct('Date of birth')}: ${friendlyDate(child.dob)}`"
                  :title="fullName(child)"
                  :value="child"
                  class="not-padded"
                />

                <ResourceForm
                  v-if="selectedChild && selectedChild.id === child.id"
                  v-model="children[index]"
                  :fields="[
                    {
                      cols: '4',
                      text: 'First Name',
                      type: 'string',
                      value: 'first_name',
                      required: true,
                    },
                    {
                      cols: '4',
                      text: 'Middle Name',
                      type: 'string',
                      value: 'middle_name',
                      required: false,
                    },
                    {
                      cols: '4',
                      text: 'Last Name',
                      type: 'string',
                      value: 'last_name',
                      required: true,
                    },
                    {
                      text: 'Date of Birth',
                      value: 'dob',
                      type: 'date',
                      required: true,
                    },
                  ]"
                />
              </div>
            </v-radio-group>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions
        :class="[
          $vuetify.display.smAndDown ? 'd-flex flex-column mb-6' : 'd-flex flex-row justify-center',
          'mt-0',
          'px-4',
          'py-4',
        ]"
      >
        <v-btn
          @click="cancel"
          :class="[
            $vuetify.display.smAndDown ? 'btn-small mb-4' : 'btn-large',
            $vuetify.display.smAndDown ? 'me-0' : 'me-3',
            'focus-very-visible',
            'd-flex',
          ]"
          class="focus-very-visible"
          color="primary"
          variant="outlined"
        >
          <span>{{ $bct(cancelButtonText) }}</span>
        </v-btn>

        <v-btn
          @click="save(selectedChild)"
          :class="[
            $vuetify.display.smAndDown ? 'btn-small mb-4' : 'btn-large',
            $vuetify.display.smAndDown ? 'me-0' : 'me-3',
            'focus-very-visible',
            'd-flex',
          ]"
          class="focus-very-visible"
          color="primary"
        >
          <span>{{ $bct(saveButtonText) }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { capitalize } from '@/plugins/filters';
import RadioButton from '@/shared/components/form/RadioButton.vue';
import ResourceForm from '@/shared/components/form/resource/ResourceForm.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

defineExpose({ close, open });

const emit = defineEmits(['cancel', 'close', 'reload-tasks']);

const $bct = inject('translator');

const { api } = useApi();
const { locale } = useI18n();
const store = useStore();

const children = ref([]);
const defaultTextClass = ref('pt-6 pb-6');
const eventBus = useEventBus();
const isVisible = ref(false);
const selectedChild = ref(null);
const waitListOfferId = ref(null);

const props = defineProps({
  cancelButtonText: {
    type: String,
    default: 'Cancel',
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  closeOnSave: {
    type: Boolean,
    default: false,
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: [String, Number],
    default: '800',
  },
  saveButtonText: {
    type: String,
    default: 'Save',
  },
  scrim: {
    type: Boolean,
    default: true,
  },
  textClass: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: 'Please make a selection to continue',
  },
  titleOverride: {
    type: String,
    default: '',
  },
});

onMounted(async () => {
  await load();
});

const instructionText = computed(() => {
  return 'Please select a child from the list. You may edit the information if incorrect.';
});

async function cancel() {
  emit('cancel');
  close();
}

function close() {
  if (isVisible.value) {
    isVisible.value = false;
    emit('close');
  }
}

function friendlyDate(date) {
  if (!date) return '';

  const dateObject = new Date(date);

  return dateObject.toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'utc',
  });
}

function fullName(child) {
  let result = capitalize(child.first_name);
  if (child.middle_name) result += ` ${capitalize(child.middle_name)}`;
  result += ` ${capitalize(child.last_name)}`;

  return result;
}

async function load() {
  await loadChildren();
}

async function loadChildren() {
  const resp = await api.parent.children.index(store.state.profile.group_id);
  children.value = resp.data;
}

async function maybeClose() {
  if (props.closeable) close();
}

function open(id) {
  isVisible.value = true;
  selectedChild.value = null;
  waitListOfferId.value = id;
}

function radioButtonGap(index) {
  return index === 0 ? null : 5;
}

async function save(selectedChild) {
  if (!selectedChild) {
    eventBus.chime('Please select a child to continue.');
    return;
  }

  const data = {
    accepted_at: new Date().toISOString(),
    child_birthdate: selectedChild.dob,
    child_first_name: selectedChild.first_name,
    child_id: selectedChild.id,
    child_middle_name: selectedChild.middle_name,
    child_last_name: selectedChild.last_name,
  };

  const response = await api.organization.waitlistOffer.update(waitListOfferId.value, data);

  if (response.status !== 200) return;

  if (props.closeOnSave) close();

  eventBus.chime('Accepted');
  emit('reload-tasks');
}
</script>

<style scoped>
:deep(div.v-card.not-padded div.v-card-text) {
  padding: 0 0 0.75rem 0 !important;
}

:deep(div.v-card-text span.fs-12) {
  color: black;
  font-size: 1rem !important;
  line-height: 2;
}
</style>
